import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Loader2, Users, BookOpen, Download, CheckCircle, XCircle, Eye, ToggleLeft, ToggleRight, Pencil, Save, X, MessageSquare, UserPlus, Calendar } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import { Input } from '@/components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * V4 PLATFORM - UNIFIED ADMIN DASHBOARD
 * Complete clean rebuild - Single source of truth for all admin operations
 */
const AdminDashboardV4 = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tutorFilter, setTutorFilter] = useState('all');
  const [studentFilter, setStudentFilter] = useState('all');
  const [processing, setProcessing] = useState(false);
  const [editingDisplayNameId, setEditingDisplayNameId] = useState(null);
  const [displayNameDraft, setDisplayNameDraft] = useState('');
  const [savingDisplayName, setSavingDisplayName] = useState(false);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [tutorsRes, studentsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/v4/admin/tutors`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/v4/admin/students`, { credentials: 'include' })
      ]);
      
      const tutorsData = await tutorsRes.json();
      const studentsData = await studentsRes.json();
      
      setTutors(tutorsData.tutors || []);
      setStudents(studentsData.students || []);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load data');
      setLoading(false);
    }
  };
  
  // Tutor actions
  const handleApproveTutor = async (tutorId, name) => {
    if (!window.confirm(`Approve ${name} and make them public?`)) return;
    setProcessing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v4/admin/tutors/${tutorId}/approve`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchData();
      } else {
        throw new Error(data.detail);
      }
    } catch (error) {
      toast.error('Failed to approve tutor');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleRejectTutor = async (tutorId, name) => {
    if (!window.confirm(`Reject ${name}?`)) return;
    setProcessing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v4/admin/tutors/${tutorId}/reject`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Tutor rejected');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to reject tutor');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleToggleTutor = async (tutorId, name) => {
    setProcessing(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v4/admin/tutors/${tutorId}/toggle`, {
        method: 'PATCH',
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to toggle tutor');
    } finally {
      setProcessing(false);
    }
  };

  const startEditDisplayName = (tutor) => {
    setEditingDisplayNameId(tutor.id);
    setDisplayNameDraft(tutor.directory_display_name || '');
  };

  const cancelEditDisplayName = () => {
    setEditingDisplayNameId(null);
    setDisplayNameDraft('');
  };

  const saveDisplayName = async (tutor) => {
    const trimmed = (displayNameDraft || '').trim();
    if (trimmed.length > 100) {
      toast.error('Display name must be 100 characters or fewer');
      return;
    }
    try {
      setSavingDisplayName(true);
      const res = await fetch(
        `${BACKEND_URL}/api/v4/admin/tutors/${tutor.id}/display-name`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ directory_display_name: trimmed }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to save display name');
      }
      toast.success(
        trimmed
          ? `Display name updated to "${trimmed}"`
          : 'Display name cleared — directory will show original name'
      );
      // Optimistic local update
      setTutors((prev) =>
        prev.map((t) =>
          t.id === tutor.id ? { ...t, directory_display_name: trimmed } : t
        )
      );
      cancelEditDisplayName();
    } catch (error) {
      console.error('Save display name error:', error);
      toast.error(error.message || 'Failed to save display name');
    } finally {
      setSavingDisplayName(false);
    }
  };
  
  const downloadCV = (cvUrl, name) => {
    // Extract filename from cv_url (format: /uploads/v4/cvs/filename.pdf)
    const filename = cvUrl.split('/').pop();
    
    // Use API endpoint which routes through ingress properly
    const apiUrl = `${BACKEND_URL}/api/v4/cv/${filename}`;
    
    window.open(apiUrl, '_blank');
    toast.success(`Opening CV for ${name}`);
  };
  
  // Student actions
  const handleAssignTutor = async (enquiryId, tutorId, studentName) => {
    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('tutor_id', tutorId);
      
      const response = await fetch(`${BACKEND_URL}/api/v4/admin/students/${enquiryId}/assign`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message);
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to assign tutor');
    } finally {
      setProcessing(false);
    }
  };
  
  const getStatusBadge = (status) => {
    const config = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      active: 'bg-blue-100 text-blue-800',
      inactive: 'bg-gray-100 text-gray-800',
      new: 'bg-yellow-100 text-yellow-800',
      contacted: 'bg-blue-100 text-blue-800',
      assigned: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={config[status] || config.pending}>{status}</Badge>;
  };
  
  const filteredTutors = tutorFilter === 'all' 
    ? tutors 
    : tutors.filter(t => t.status === tutorFilter);
    
  const filteredStudents = studentFilter === 'all'
    ? students
    : students.filter(s => s.status === studentFilter);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Admin Dashboard V4</h1>
            <p className="text-gray-600 mt-2">Clean platform rebuild - Manage tutors and students</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="bg-black rounded-full px-2 h-10 flex items-center"><NotificationBell accent="gold" testidPrefix="admin-v4-" /></div>
            <Button onClick={() => navigate('/admin/access-requests')} variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50" data-testid="nav-access-requests">
              <UserPlus className="h-4 w-4 mr-2" />
              Access Requests
            </Button>
            <Button onClick={() => navigate('/admin/booking-requests')} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="nav-booking-requests">
              <Calendar className="h-4 w-4 mr-2" />
              Bookings
            </Button>
            <Button onClick={() => navigate('/staff/dashboard')} className="bg-red-700 hover:bg-red-800" data-testid="nav-staff-hub">
              <MessageSquare className="h-4 w-4 mr-2" />
              Staff Hub
            </Button>
          </div>
        </div>

        <Phase4CKpisV4 />
        <Tabs defaultValue="tutors" className="space-y-6">
          <TabsList>
            <TabsTrigger value="tutors" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Tutors ({tutors.length})
            </TabsTrigger>
            <TabsTrigger value="students" className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Students ({students.length})
            </TabsTrigger>
          </TabsList>
          
          {/* TUTORS TAB */}
          <TabsContent value="tutors" className="space-y-6">
            <div className="flex justify-between items-center">
              <Select value={tutorFilter} onValueChange={setTutorFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tutors</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-6">
              {filteredTutors.map(tutor => (
                <Card key={tutor.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-xl">{tutor.full_name}</CardTitle>
                        {/* Directory Display Name — admin-editable public-facing name */}
                        <div className="mt-2" data-testid={`display-name-row-${tutor.id}`}>
                          {editingDisplayNameId === tutor.id ? (
                            <div className="flex items-center gap-2 flex-wrap">
                              <Input
                                value={displayNameDraft}
                                onChange={(e) => setDisplayNameDraft(e.target.value)}
                                placeholder='e.g. "Gardner W."'
                                maxLength={100}
                                className="h-8 max-w-xs"
                                data-testid={`display-name-input-${tutor.id}`}
                                autoFocus
                              />
                              <Button
                                size="sm"
                                className="h-8 bg-[#007A7A] hover:bg-[#006060] text-white gap-1"
                                onClick={() => saveDisplayName(tutor)}
                                disabled={savingDisplayName}
                                data-testid={`save-display-name-${tutor.id}`}
                              >
                                {savingDisplayName ? (
                                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                  <Save className="h-3.5 w-3.5" />
                                )}
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-8 gap-1"
                                onClick={cancelEditDisplayName}
                                disabled={savingDisplayName}
                                data-testid={`cancel-display-name-${tutor.id}`}
                              >
                                <X className="h-3.5 w-3.5" />
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-gray-500 uppercase tracking-wide">
                                Directory name:
                              </span>
                              <span
                                className={`text-sm font-medium ${
                                  tutor.directory_display_name
                                    ? 'text-gray-900'
                                    : 'text-gray-400 italic'
                                }`}
                                data-testid={`display-name-value-${tutor.id}`}
                              >
                                {tutor.directory_display_name || 'Not set · uses full name'}
                              </span>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 px-2 gap-1 text-gray-600 hover:text-gray-900"
                                onClick={() => startEditDisplayName(tutor)}
                                data-testid={`edit-display-name-${tutor.id}`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                Edit
                              </Button>
                            </div>
                          )}
                        </div>
                        <CardDescription className="mt-2 space-y-1">
                          <div>{tutor.email}</div>
                          <div>{tutor.phone}</div>
                        </CardDescription>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {getStatusBadge(tutor.status)}
                        {tutor.is_public && <Badge className="bg-green-100 text-green-800">Public</Badge>}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <span className="font-semibold">Subjects: </span>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {tutor.subjects.map((s, i) => (
                          <Badge key={i} variant="outline">{s}</Badge>
                        ))}
                      </div>
                    </div>
                    {tutor.notes && (
                      <div>
                        <span className="font-semibold">Notes: </span>
                        <p className="text-sm text-gray-600 mt-1">{tutor.notes}</p>
                      </div>
                    )}
                    <div className="flex gap-2 pt-2 border-t">
                      <Button size="sm" variant="outline" onClick={() => downloadCV(tutor.cv_url, tutor.full_name)}>
                        <Download className="h-4 w-4 mr-2" /> CV
                      </Button>
                      {tutor.status === 'pending' && (
                        <>
                          <Button size="sm" className="bg-green-600" onClick={() => handleApproveTutor(tutor.id, tutor.full_name)} disabled={processing}>
                            <CheckCircle className="h-4 w-4 mr-2" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleRejectTutor(tutor.id, tutor.full_name)} disabled={processing}>
                            <XCircle className="h-4 w-4 mr-2" /> Reject
                          </Button>
                        </>
                      )}
                      {(tutor.status === 'approved' || tutor.status === 'active' || tutor.status === 'inactive') && (
                        <Button size="sm" variant="outline" onClick={() => handleToggleTutor(tutor.id, tutor.full_name)} disabled={processing}>
                          {tutor.is_public ? <ToggleRight className="h-4 w-4 mr-2" /> : <ToggleLeft className="h-4 w-4 mr-2" />}
                          {tutor.is_public ? 'Deactivate' : 'Activate'}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {filteredTutors.length === 0 && (
                <Card><CardContent className="p-12 text-center text-gray-500">No tutors found</CardContent></Card>
              )}
            </div>
          </TabsContent>
          
          {/* STUDENTS TAB */}
          <TabsContent value="students" className="space-y-6">
            <div className="flex justify-between items-center">
              <Select value={studentFilter} onValueChange={setStudentFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Enquiries</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="assigned">Assigned</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid gap-6">
              {filteredStudents.map(student => (
                <Card key={student.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl">{student.student_name}</CardTitle>
                        <CardDescription className="mt-2 space-y-1">
                          {student.parent_name && <div>Parent: {student.parent_name}</div>}
                          <div>{student.email}</div>
                          <div>{student.phone}</div>
                        </CardDescription>
                      </div>
                      {getStatusBadge(student.status)}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div><span className="font-semibold">Program:</span> {student.program_type}</div>
                      <div><span className="font-semibold">Subject:</span> {student.subject}</div>
                      <div><span className="font-semibold">Year:</span> {student.year_level}</div>
                      <div><span className="font-semibold">Format:</span> {student.preferred_format}</div>
                      <div className="col-span-2"><span className="font-semibold">Times:</span> {student.preferred_times}</div>
                    </div>
                    {student.tutor_preference === 'specific' && student.requested_tutor_name && (
                      <div className="p-3 bg-blue-50 rounded border border-blue-200">
                        <span className="font-semibold text-blue-900">Preferred Tutor:</span> 
                        <span className="ml-2 text-gray-900">{student.requested_tutor_name}</span>
                        <p className="text-xs text-blue-700 mt-1">Selected by student</p>
                      </div>
                    )}
                    {student.tutor_preference === 'match' && (
                      <div className="p-3 bg-gray-50 rounded border border-gray-200">
                        <span className="font-semibold text-gray-700">Preferred Tutor:</span> 
                        <span className="ml-2 text-gray-600">No Preference</span>
                        <p className="text-xs text-gray-500 mt-1">Student requested best match</p>
                      </div>
                    )}
                    {student.assigned_tutor_name && (
                      <div className="p-3 bg-green-50 rounded border border-green-200">
                        <span className="font-semibold text-green-900">Assigned Tutor:</span> 
                        <span className="ml-2 text-gray-900">{student.assigned_tutor_name}</span>
                        <p className="text-xs text-green-700 mt-1">Allocated by Winhouse Tutors</p>
                      </div>
                    )}
                    {student.notes && (
                      <div>
                        <span className="font-semibold">Notes:</span>
                        <p className="text-sm text-gray-600 mt-1">{student.notes}</p>
                      </div>
                    )}
                    {!student.assigned_tutor_id && (
                      <div className="pt-2 border-t">
                        <Label className="text-sm font-semibold mb-2 block">Assign Tutor:</Label>
                        <div className="flex gap-2">
                          <Select onValueChange={(tutorId) => handleAssignTutor(student.id, tutorId, student.student_name)} disabled={processing}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select tutor..." />
                            </SelectTrigger>
                            <SelectContent>
                              {tutors.filter(t => t.is_public).map(t => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.full_name} - {t.subjects.join(', ')}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
              {filteredStudents.length === 0 && (
                <Card><CardContent className="p-12 text-center text-gray-500">No enquiries found</CardContent></Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

const Phase4CKpisV4 = () => {
  const [k, setK] = React.useState(null);
  React.useEffect(() => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    let live = true;
    const load = async () => {
      try {
        const res = await fetch(BACKEND_URL + '/api/admin/analytics/snapshot', { credentials: 'include' });
        if (!res.ok) return;
        const d = await res.json();
        if (live) setK(d);
      } catch (_e) {}
    };
    load();
    const id = setInterval(load, 60000);
    return () => { live = false; clearInterval(id); };
  }, []);
  if (!k) return null;
  const items = [
    { l: 'Today bookings', v: k.today_bookings, c: 'from-amber-50 to-amber-100 text-amber-900' },
    { l: 'Pending approvals', v: k.pending_approvals, c: 'from-red-50 to-red-100 text-red-900' },
    { l: 'Upcoming', v: k.upcoming, c: 'from-blue-50 to-blue-100 text-blue-900' },
    { l: 'Total bookings', v: k.total_bookings, c: 'from-gray-50 to-gray-100 text-gray-900' },
    { l: 'Completion %', v: k.completion_rate_pct == null ? '—' : `${k.completion_rate_pct}%`, c: 'from-emerald-50 to-emerald-100 text-emerald-900' },
    { l: 'Active tutors', v: k.active_tutors, c: 'from-purple-50 to-purple-100 text-purple-900' },
    { l: 'Active students', v: k.active_students, c: 'from-sky-50 to-sky-100 text-sky-900' },
    { l: 'Recaps generated', v: k.recaps_generated, c: 'from-yellow-50 to-yellow-100 text-yellow-900' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 mb-6" data-testid="phase4c-kpis">
      {items.map((it) => (
        <div key={it.l} className={`bg-gradient-to-br ${it.c} rounded-xl p-3 border border-white/40`} data-testid={`kpi-${it.l.replace(/\s+/g, '-').toLowerCase()}`}>
          <div className="text-[9px] uppercase tracking-wider font-bold opacity-70">{it.l}</div>
          <div className="text-xl font-bold mt-1">{it.v}</div>
        </div>
      ))}
    </div>
  );
};

export default AdminDashboardV4;
